import { Handle, Position } from '@xyflow/react';
import type { NodeBase } from '@/types/assets';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Server,
  Cpu,
  Shield,
  Cloud,
  Building,
  Users,
  Database,
  Router,
  Wifi,
  HardDrive,
} from 'lucide-react';
import { useAssetsStore } from '@/stores/assetsStore';

interface AssetNodeData {
  id: NodeBase['id'];
  type: NodeBase['type'] | 'asset' | 'cloud' | 'datastore';
  label: NodeBase['label'];
  domain?: NodeBase['domain'];
  purdue_level?: NodeBase['purdue_level'];
  category?: NodeBase['category'];
  parentId?: NodeBase['parentId'];
  position?: NodeBase['position'];
  meta?: NodeBase['meta'];
}

interface AssetNodeProps {
  data: AssetNodeData;
  selected: boolean;
}

const getNodeIcon = (type: AssetNodeData['type'], category?: string) => {
  switch (type) {
    case 'site':
      return <Building className="h-4 w-4" />;
    case 'zone':
      return <Shield className="h-4 w-4" />;
    case 'cloud':
      return <Cloud className="h-4 w-4" />;
    case 'supplier':
      return <Users className="h-4 w-4" />;
    case 'datastore':
      return <Database className="h-4 w-4" />;
    case 'asset':
    case 'system':
      if (category === 'Switch' || category === 'Router') return <Router className="h-4 w-4" />;
      if (category === 'AP') return <Wifi className="h-4 w-4" />;
      if (category === 'Server' || category === 'VM') return <Server className="h-4 w-4" />;
      if (category === 'PLC' || category === 'HMI') return <Cpu className="h-4 w-4" />;
      if (category?.includes('DB') || category?.includes('Database')) return <Database className="h-4 w-4" />;
      if (category?.includes('Storage') || category?.includes('NAS')) return <HardDrive className="h-4 w-4" />;
      return <Server className="h-4 w-4" />;
    default:
      return <Server className="h-4 w-4" />;
  }
};

const getDomainColor = (domain?: NodeBase['domain']) => {
  switch (domain) {
    case 'IT':
      return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
    case 'OT':
      return 'bg-orange-500/10 text-orange-700 border-orange-500/20';
    case 'Cloud':
      return 'bg-purple-500/10 text-purple-700 border-purple-500/20';
    case 'External':
      return 'bg-gray-500/10 text-gray-700 border-gray-500/20';
    default:
      return 'bg-gray-500/10 text-gray-700 border-gray-500/20';
  }
};

const getCriticalityColor = (criticality?: NonNullable<NodeBase['meta']>['criticality']) => {
  switch (criticality) {
    case 'H':
      return 'bg-red-500/10 text-red-700 border-red-500/20';
    case 'M':
      return 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20';
    case 'L':
      return 'bg-green-500/10 text-green-700 border-green-500/20';
    default:
      return '';
  }
};

export function AssetNode({ data, selected }: AssetNodeProps) {
  const { getRisksByNode, getTasksByNode, setSelectedNode } = useAssetsStore();

  const risks = getRisksByNode(data.id);
  const tasks = getTasksByNode(data.id);
  const openRisks = risks.filter((risk) => risk.status === 'Open');
  const dueTasks = tasks.filter((task) => task.status === 'Due' || task.status === 'Overdue');

  const handleClick = () => {
    setSelectedNode(data.id);
  };

  return (
    <Card
      className={`min-w-[200px] cursor-pointer transition-all ${selected ? 'ring-2 ring-primary shadow-lg' : 'hover:shadow-md'}`}
      onClick={handleClick}
    >
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          {getNodeIcon(data.type, data.category)}
          <div className="flex-1">
            <div className="font-medium text-sm truncate">{data.label}</div>
            <div className="text-xs text-muted-foreground capitalize">{data.type}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 mb-2">
          {data.domain && (
            <Badge variant="outline" className={getDomainColor(data.domain)}>
              {data.domain}
            </Badge>
          )}
          {data.purdue_level && (
            <Badge variant="outline" className="text-xs">
              {data.purdue_level}
            </Badge>
          )}
          {data.meta?.criticality && (
            <Badge variant="outline" className={getCriticalityColor(data.meta.criticality)}>
              {data.meta.criticality}
            </Badge>
          )}
        </div>

        {(openRisks.length > 0 || dueTasks.length > 0) && (
          <div className="flex gap-1">
            {openRisks.length > 0 && (
              <Badge variant="destructive" className="text-xs">
                {openRisks.length} Risk{openRisks.length > 1 ? 's' : ''}
              </Badge>
            )}
            {dueTasks.length > 0 && (
              <Badge variant="secondary" className="text-xs bg-warning/10 text-warning-foreground border-warning/20">
                {dueTasks.length} Due
              </Badge>
            )}
          </div>
        )}

        {data.meta?.ip && <div className="text-xs text-muted-foreground mt-1">{data.meta.ip}</div>}
      </div>

      <Handle
        type="source"
        position={Position.Top}
        className="w-3 h-3 border-2 border-primary bg-background hover:bg-primary/20"
        id="top"
        isConnectable
      />
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 border-2 border-primary bg-background hover:bg-primary/20"
        id="top"
        isConnectable
        style={{ visibility: 'hidden' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 border-2 border-primary bg-background hover:bg-primary/20"
        id="bottom"
        isConnectable
      />
      <Handle
        type="target"
        position={Position.Bottom}
        className="w-3 h-3 border-2 border-primary bg-background hover:bg-primary/20"
        id="bottom"
        isConnectable
        style={{ visibility: 'hidden' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        className="w-3 h-3 border-2 border-primary bg-background hover:bg-primary/20"
        id="left"
        isConnectable
      />
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 border-2 border-primary bg-background hover:bg-primary/20"
        id="left"
        isConnectable
        style={{ visibility: 'hidden' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 border-2 border-primary bg-background hover:bg-primary/20"
        id="right"
        isConnectable
      />
      <Handle
        type="target"
        position={Position.Right}
        className="w-3 h-3 border-2 border-primary bg-background hover:bg-primary/20"
        id="right"
        isConnectable
        style={{ visibility: 'hidden' }}
      />
    </Card>
  );
}